import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/error-boundary";
import "./App.css";
import PrivateRoute from "./authentication";
import PublicRoute from "./authentication/PublicRoute";
import RootRedirect from "./authentication/RootRedirect";
import LoadingSpinner from "./components/loading-spinner";
import { SubscriptionSync } from "./context/SubscriptionSync";
import { SidebarProvider } from "./context/SidebarContext";
import PodcastUploadStatusBar from "./components/podcast-upload-status-bar";
import ScrollToTop from "./components/scroll-to-top";
import MetaPixelPageView from "./components/meta-pixel-page-view";

// Lazy load all components
const CommunityPage = lazy(() => import("./pages/community"));
const FeedPage = lazy(() => import("./pages/feed"));
const ForgetPasswordPage = lazy(() => import("./pages/forget-password"));
const MyProfilePage = lazy(() => import("./pages/my-profile"));
const ResetPasswordPage = lazy(() => import("./pages/reset-password"));
const SigninPage = lazy(() => import("./pages/sign-in"));
const SignUpRoute = lazy(() => import("./pages/home/SigupRoute"));
const SignupPaymentSuccessPage = lazy(
  () => import("./pages/sign-up/PaymentSuccess"),
);
const VerifyEmailPage = lazy(() => import("./pages/verify-email"));
const SavedPosts = lazy(() => import("@src/components/saved-posts"));
const ViewPost = lazy(() => import("@src/components/view-post"));
const Connections = lazy(() => import("./pages/connections"));
const SpecificCommunity = lazy(
  () => import("./pages/community/SpecificCommunity"),
);
const UserMessages = lazy(() => import("./pages/Massages"));

const InlineArticleView = lazy(
  () => import("@src/pages/rss/InlineArticleView"),
);
const Search = lazy(() => import("@src/pages/search"));
const MyPosts = lazy(() => import("@src/components/my-posts"));
const MyImages = lazy(() => import("./components/my-images"));
const MyVideos = lazy(() => import("./components/my-videos"));
const MyComments = lazy(() => import("./components/my-comments"));
const RssSourcePage = lazy(() =>
  import("@src/pages/rss").then((module) => ({
    default: module.RssSourcePage,
  })),
);
const GroupDashboard = lazy(
  () => import("./pages/groups/components/GroupDashboard"),
);
const GroupPage = lazy(() => import("./pages/groups/index"));
const GroupMembersList = lazy(
  () => import("./pages/groups/components/GroupMembersList"),
);
const AllMentors = lazy(() => import("./pages/mentors"));
const Posts = lazy(() => import("@src/pages/search/posts"));
const People = lazy(() => import("@src/pages/search/people"));
const SavedRSS = lazy(() => import("./components/saved-rss"));
const GroupSettings = lazy(
  () => import("./pages/groups/components/GroupSettings"),
);
const NewArticlePage = lazy(() => import("./pages/article/NewArticlePage"));
const EditArticlePage = lazy(() => import("./pages/article/EditArticlePage"));
const ArticleView = lazy(() => import("./pages/article/ArticleView"));
const CompanyPage = lazy(() => import("./pages/company"));
const AllCompaniesPage = lazy(() => import("./pages/company/AllCompaniesPage"));
const CompanyDashboard = lazy(() => import("./pages/company/CompanyDashboard"));
const CompanyInbox = lazy(() => import("./pages/company/CompanyInbox"));
const EventsPage = lazy(() => import("./pages/event/EventListing"));
const CreateEventForm = lazy(() => import("./components/event-form"));
const EventDashboard = lazy(() => import("./pages/event/EventDashboard"));
const AudioGroupCall = lazy(
  () => import("./components/livekit/Audio-Call-interface"),
);
const CallPage = lazy(() => import("./pages/Massages/CallPage"));
const ManageCourses = lazy(() => import("./pages/courses/ManageCourses"));
const AllCourses = lazy(() => import("./pages/courses/AllCourses"));
const CreateCourse = lazy(() => import("./pages/courses/CreateCourse"));
const CourseDashboard = lazy(() => import("./pages/courses/CourseDashboard"));
const ViewCourse = lazy(() => import("./pages/courses/ViewCourse"));
const CourseAnalytics = lazy(() => import("./pages/courses/CourseAnalytics"));
const CeCoursesPage = lazy(() => import("./pages/ce-courses"));
const CreateLiveStream = lazy(() => import("./pages/live/CreateLiveStream"));
const LiveStreamViewer = lazy(() => import("./pages/live/LiveStreamViewer"));
const AllServices = lazy(() => import("./pages/services/all-services"));
const JobsPage = lazy(() => import("./pages/jobs"));
const JobDetailPage = lazy(() => import("./pages/jobs/job-detail"));
const MyServices = lazy(() => import("./pages/services/my-services"));
const ManageService = lazy(
  () => import("./pages/services/my-services/components/ManageService"),
);
const ServiceDetailPage = lazy(
  () => import("./pages/services/detail-page/index"),
);
const ServiceCategoriesPage = lazy(
  () => import("./pages/services/all-services/components/CategoriesPage"),
);
const ReelsPage = lazy(() => import("./pages/reels"));
const UserReelsPage = lazy(
  () => import("./pages/reels/components/UserReelsPage"),
);
const PodcastsPage = lazy(() => import("./pages/podcasts"));
const PodcastProfilePage = lazy(
  () => import("./pages/podcasts/PodcastProfileScreen"),
);
const PodcastEpisodePage = lazy(
  () => import("./pages/podcasts/PodcastEpisodePage"),
);
const AiChatPage = lazy(() => import("./pages/ai-chat"));
const SupportHubPage = lazy(() => import("./pages/support/SupportHubPage"));
const CreateTicketPage = lazy(() => import("./pages/support/CreateTicketPage"));
const MyTicketsPage = lazy(() => import("./pages/support/MyTicketsPage"));
const TicketDetailPage = lazy(() => import("./pages/support/TicketDetailPage"));
const SupportArticlePage = lazy(() => import("./pages/support/ArticlePage"));
const NotificationPage = lazy(() => import("./pages/notification"));
const SavedPage = lazy(() => import("./pages/saved"));
const LibraryPage = lazy(() => import("./pages/library"));
const LibraryFolderPage = lazy(() => import("./pages/library/folder"));
const LibraryCollectionPage = lazy(() => import("./pages/library/collection"));
const InfluencePage = lazy(() => import("./pages/influence"));
const CouponDetailPage = lazy(
  () => import("./pages/services/my-services/CouponDetailPage"),
);
const SettingsPage = lazy(() => import("./pages/settings"));
const SettingsProfileTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.SettingsProfileTab,
  })),
);
const SettingsDigitalCardTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.SettingsDigitalCardTab,
  })),
);
const SettingsReferralTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.SettingsReferralTab,
  })),
);
const SettingsRoleTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.SettingsRoleTab,
  })),
);
const SettingsSessionsTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.SettingsSessionsTab,
  })),
);
const SettingsPasswordTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.SettingsPasswordTab,
  })),
);
const BillingInvoicesTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.BillingInvoicesTab,
  })),
);
const BillingSubscriptionTab = lazy(() =>
  import("./pages/settings/tabs").then((m) => ({
    default: m.BillingSubscriptionTab,
  })),
);
const MentorPaymentsSettingsTab = lazy(
  () => import("./pages/settings/components/MentorPaymentsSettingsTab"),
);
const IndustryPartnerBillingSettingsTab = lazy(
  () => import("./pages/settings/components/IndustryPartnerBillingSettingsTab"),
);
const StripeConnectTab = lazy(
  () => import("./pages/setting/sub-components/stripe-connect"),
);
const MySessionsPage = lazy(() => import("./pages/my-sessions"));
const SettingPage = lazy(() => import("./pages/setting"));
const StripeBillingSuccessPage = lazy(() => import("./pages/stripe-billing-success"));
const MentorEarningsPage = lazy(() => import("./pages/mentor-earnings"));
const AdsManager = lazy(() => import("./pages/ads-manager"));
const CreateCampaign = lazy(() => import("./pages/ads-manager/CreateCampaign"));
const EditCampaign = lazy(() => import("./pages/ads-manager/EditCampaign"));
const AdAnalytics = lazy(() => import("./pages/ads-manager/AdAnalytics"));
const BoostProfilePage = lazy(() => import("./pages/boost-profile"));
const CreateBoostProfilePage = lazy(() => import("./pages/boost-profile/CreateBoost"));
const BoostProfileDetailPage = lazy(() => import("./pages/boost-profile/BoostDetail"));
const PostBoostHubPage = lazy(() => import("./pages/boost-post"));
const CreatePostBoostPage = lazy(() => import("./pages/boost-post/CreatePostBoost"));
const PostBoostDetailPage = lazy(() => import("./pages/boost-post/BoostDetail"));
const PlansPage = lazy(() => import("./pages/plans"));
const PlansSuccessPage = lazy(() => import("./pages/plans/success"));


// Loading fallback component
const LoadingFallback = () => (
  <div className="flex justify-center items-center h-screen">
    <LoadingSpinner width={64} height={64} />
  </div>
);

function App() {
  return (
    <SidebarProvider>
      <Router>
        <ScrollToTop />
        <MetaPixelPageView />
        <SubscriptionSync />
        {/* Rendered inside Router (but outside Routes) so it survives route changes and can use <Link> */}
        <PodcastUploadStatusBar />
        <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/sign-up" element={<SignUpRoute />} />
            <Route
              path="/sign-up/payment-success"
              element={<SignupPaymentSuccessPage />}
            />
            <Route path="/sign-in" element={<SigninPage />} />
            <Route path="/forget-password" element={<ForgetPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* <Route path="/" element={<Landingpage />} /> */}
            {/* <Route path="/all-services" element={<AllPublicServices />} /> */}

            {/* Public routes — accessible without authentication, but resolve the
                session first so logged-in users get their authenticated view */}
            <Route element={<PublicRoute />}>
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route
                path="/service-categories"
                element={<ServiceCategoriesPage />}
              />
              <Route path="/services" element={<AllServices />} />
              <Route path="/services/:id" element={<ServiceDetailPage />} />
              <Route path="/view-post/:postId" element={<ViewPost />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/jobs/:jobId" element={<JobDetailPage />} />
            </Route>

            <Route element={<PrivateRoute />}>
              <Route path="/feed" element={<FeedPage />} />
              <Route path="/my-profile" element={<MyProfilePage />} />
              <Route path="/settings" element={<SettingsPage />}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<SettingsProfileTab />} />
                <Route path="digital-card" element={<SettingsDigitalCardTab />} />
                <Route path="referral" element={<SettingsReferralTab />} />
                <Route path="role" element={<SettingsRoleTab />} />
                <Route path="sessions" element={<SettingsSessionsTab />} />
                <Route path="payments" element={<MentorPaymentsSettingsTab />}>
                  <Route index element={<Navigate to="stripe" replace />} />
                  <Route path="stripe" element={<StripeConnectTab embedded />} />
                  <Route
                    path="transactions"
                    element={<Navigate to="/settings/invoices" replace />}
                  />
                </Route>
                <Route path="password" element={<SettingsPasswordTab />} />
                <Route path="invoices" element={<BillingInvoicesTab />} />
                <Route path="billing" element={<IndustryPartnerBillingSettingsTab />}>
                  <Route index element={<Navigate to="subscription" replace />} />
                  <Route path="subscription" element={<BillingSubscriptionTab />} />
                  <Route path="invoices" element={<Navigate to="/settings/invoices" replace />} />
                </Route>
                <Route path="*" element={<Navigate to="profile" replace />} />
              </Route>
              <Route path="/my-sessions" element={<MySessionsPage />} />
              <Route path="/saved-posts" element={<SavedPosts />} />
              <Route path="/saved-rss" element={<SavedRSS />} />
              <Route path="/saved" element={<SavedPage />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/library/folder/:folderId" element={<LibraryFolderPage />} />
              <Route path="/library/collection/:collectionId" element={<LibraryCollectionPage />} />
              <Route path="/influence" element={<InfluencePage />} />
              <Route path="/notifications" element={<NotificationPage />} />
              <Route path="/reels" element={<ReelsPage />} />
              <Route path="/reels/user" element={<UserReelsPage />} />
              <Route path="/podcasts" element={<PodcastsPage />} />
              <Route
                path="/podcasts/episode/:episodeId"
                element={<PodcastEpisodePage />}
              />
              <Route
                path="/podcasts/:userId"
                element={<PodcastProfilePage />}
              />
              <Route path="/ai-chat" element={<AiChatPage />} />
              <Route path="/support" element={<SupportHubPage />} />
              <Route path="/support/new" element={<CreateTicketPage />} />
              <Route path="/support/tickets" element={<MyTicketsPage />} />
              <Route path="/support/tickets/:id" element={<TicketDetailPage />} />
              <Route path="/support/articles/:slug" element={<SupportArticlePage />} />
              <Route path="/search" element={<Search />} />
              <Route path="/search-posts" element={<Posts />} />
              <Route path="/search-people" element={<People />} />
              {/* <Route path="/Profile-Setting" element={<ProfileSettings />} /> */}
              {/* <Route path="/view-post/:postId" element={<ViewPost />} /> */}
              <Route path="/connections" element={<Connections />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/:id" element={<SpecificCommunity />} />
              <Route path="/messages" element={<UserMessages />} />
              <Route path="/mentor" element={<AllMentors />} />
              <Route path="/groups" element={<GroupPage />} />
              <Route path="/groups/:id" element={<GroupDashboard />} />
              <Route
                path="/groups/:id/members"
                element={<GroupMembersList />}
              />
              <Route path="/groups/:id/settings" element={<GroupSettings />} />
              <Route path="/article/new" element={<NewArticlePage />} />
              <Route path="/article/edit/:id" element={<EditArticlePage />} />
              <Route path="/article/:slug" element={<ArticleView />} />
              {/* RSS Article Route */}
              <Route path="/rss/article/:id" element={<InlineArticleView />} />
              <Route path="/rss/source" element={<RssSourcePage />} />
              <Route path="/my-posts" element={<MyPosts />} />
              <Route path="/my-images" element={<MyImages />} />
              <Route path="/my-videos" element={<MyVideos />} />
              <Route path="/my-comments" element={<MyComments />} />
              <Route path="/companies" element={<CompanyPage />} />
              <Route path="/companies/all" element={<AllCompaniesPage />} />
              <Route
                path="/company/:companyId"
                element={<CompanyDashboard />}
              />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/create-event" element={<CreateEventForm />} />
              <Route path="/events/:eventId" element={<EventDashboard />} />
              <Route path="/call" element={<CallPage />} />
              <Route
                path="/create-live-stream"
                element={<CreateLiveStream />}
              />
              <Route path="/live-stream/:roomId" element={<LiveStreamViewer />} />
              <Route path="/live-stream" element={<LiveStreamViewer />} />
              <Route path="/courses" element={<AllCourses />} />
              <Route path="/ce-courses" element={<CeCoursesPage />} />
              <Route path="/manage-courses" element={<ManageCourses />} />
              <Route path="/create-courses" element={<CreateCourse />} />
              <Route
                path="/edit-courses/:courseId"
                element={<CreateCourse />}
              />

              

              <Route path="/course/:courseId" element={<CourseDashboard />} />
              <Route path="/view-course/:courseId" element={<ViewCourse />} />
              <Route
                path="/course-analytics/:courseId"
                element={<CourseAnalytics />}
              />
              {/* <Route path="/course-categories" element={<CourseCategories />} /> */}
              <Route
                path="/company/:companyId/admin/inbox"
                element={<CompanyInbox />}
              />
              <Route
                path="/events/:eventId/edit-event"
                element={<CreateEventForm />}
              />
              <Route
                path="/events/:eventId/audio-call/"
                element={<AudioGroupCall />}
              />
              <Route path="/my-services" element={<MyServices />} />
              <Route path="/manage-service" element={<ManageService />} />
              <Route
                path="/coupon-detail/:serviceId"
                element={<CouponDetailPage />}
              />

              <Route path="/setting" element={<SettingPage />} />
              <Route path="/stripe-billing-success" element={<StripeBillingSuccessPage />} />
              <Route path="/mentor-earnings" element={<MentorEarningsPage />} />
              {/* Plans – Industry Partners only */}
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/plans/success" element={<PlansSuccessPage />} />
              <Route
                path="/invoices"
                element={<Navigate to="/settings/invoices" replace />}
              />
              {/* Ads Manager – Industry Partners & Super Admin */}
              <Route path="/ads-manager" element={<AdsManager />} />
              <Route path="/ads-manager/create-campaign" element={<CreateCampaign />} />
              <Route path="/ads-manager/edit-campaign/:id" element={<EditCampaign />} />
              <Route path="/ads-manager/ad/:id" element={<AdAnalytics />} />
              {/* Profile Boost – Mentors */}
              <Route path="/boost-profile" element={<BoostProfilePage />} />
              <Route path="/boost-profile/new" element={<CreateBoostProfilePage />} />
              <Route path="/boost-profile/:id" element={<BoostProfileDetailPage />} />
              {/* Post Boost */}
              <Route path="/boost-post" element={<PostBoostHubPage />} />
              <Route path="/boost-post/new" element={<CreatePostBoostPage />} />
              <Route path="/boost-post/:id" element={<PostBoostDetailPage />} />
              {/* 404 Page can be added here */}
              <Route
                path="*"
                element={
                  <div className="flex justify-center items-center h-screen text-red-500 text-6xl">
                    404 Not Found
                  </div>
                }
              ></Route>
            </Route>
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </Router>
    </SidebarProvider>
  );
}

export default App;
